/* wishlist.js — Optimized + GA4 Analytics
 * ─────────────────────────────────────────────────────────────────────────
 *  Changes vs original:
 *  • In-memory cache for handles + product data (zero extra localStorage reads)
 *  • wishlist:updated is single source of truth — no double-sync on toggle
 *  • renderWishlistPage is gated: only runs on wishlist page, never doubles up
 *  • Remove buttons use event delegation on grid (no per-card listeners)
 *  • MutationObserver: nodes accumulated across all batches in debounce window
 *    (fixes lost mutations when AJAX/filters add multiple cards within 50ms)
 *  • Cross-tab storage event skips re-render on non-wishlist pages
 *  • Full GA4 ecommerce events: add, remove, view, clear, ATC-from-wishlist
 * ─────────────────────────────────────────────────────────────────────────
 */
(() => {
  const STORAGE_KEY   = 'wishlist:handles:v1';
  const DATA_KEY      = 'wishlist:data:v1';
  const WISHLIST_URL  = '/pages/wishlist';

  const sel = {
    headerCount     : '[data-wl-count]',
    toggleBtn       : '[data-wl-toggle]',
    wishlistGrid    : '[data-wl-grid]',
    wishlistEmpty   : '[data-wl-empty]',
    wishlistClear   : '[data-wl-clear]',
    wishlistCount   : '[data-wl-page-count]',
    wishlistRemove  : '[data-wl-remove]',
    wishlistLoading : '[data-wl-loading]',
  };

  // ── In-memory cache ────────────────────────────────────────────────────
  let _handles = null;
  let _data    = null;

  function safeParse(str, fallback) {
    try { return JSON.parse(str); } catch { return fallback; }
  }

  function readHandles() {
    if (_handles !== null) return _handles;
    const arr = safeParse(localStorage.getItem(STORAGE_KEY), []);
    _handles = Array.isArray(arr) ? arr.filter(Boolean) : [];
    return _handles;
  }

  function readData() {
    if (_data !== null) return _data;
    const obj = safeParse(localStorage.getItem(DATA_KEY), {});
    _data = (obj && typeof obj === 'object') ? obj : {};
    return _data;
  }

  function persistHandles(arr) {
    const uniq = [...new Set(arr)];
    _handles = uniq;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(uniq));
  }

  function persistData(map) {
    _data = map;
    localStorage.setItem(DATA_KEY, JSON.stringify(map));
  }

  function getHandles()         { return readHandles(); }
  function isInWishlist(handle) { return readHandles().includes(handle); }
  function getItemData(handle)  { return readData()[handle] || {}; }

  // ── Product data ────────────────────────────────────────────────────────
  function extractItemData(btn) {
    return {
      id       : btn.dataset.productId  || '',
      name     : btn.dataset.title      || '',
      price    : (parseInt(btn.dataset.price, 10) || 0) / 100,
      brand    : btn.dataset.vendor     || '',
      category : btn.dataset.category   || '',
      variantId: btn.dataset.variantId  || '',
    };
  }

  function storeItemData(handle, data) {
    persistData({ ...readData(), [handle]: data });
  }

  // ── GA4 Analytics ──────────────────────────────────────────────────────
  const _currency = window.Shopify?.currency?.active || 'USD';

  function _gtag(eventName, params) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...params });
    }
  }

  function _buildItem(handle, data) {
    return {
      item_id      : data.id        || handle,
      item_name    : data.name      || handle,
      item_brand   : data.brand     || undefined,
      item_category: data.category  || undefined,
      price        : data.price     || 0,
      quantity     : 1,
    };
  }

  const GA4 = {
    addToWishlist(handle, data) {
      _gtag('add_to_wishlist', {
        currency: _currency,
        value   : data.price || 0,
        items   : [_buildItem(handle, data)],
      });
    },

    removeFromWishlist(handle, data) {
      _gtag('remove_from_wishlist', {
        currency: _currency,
        value   : data.price || 0,
        items   : [_buildItem(handle, data)],
      });
    },

    viewWishlist() {
      const handles = getHandles();
      _gtag('view_wishlist', {
        currency     : _currency,
        wishlist_size: handles.length,
        items        : handles.map(h => _buildItem(h, getItemData(h))),
      });
    },

    clearAll(handles) {
      _gtag('wishlist_clear_all', {
        currency     : _currency,
        wishlist_size: handles.length,
        items        : handles.map(h => _buildItem(h, getItemData(h))),
      });
    },

    wishlistToCart(handle) {
      const data = getItemData(handle);
      _gtag('wishlist_to_cart', {
        currency: _currency,
        value   : data.price || 0,
        items   : [_buildItem(handle, data)],
      });
    },
  };

  // ── State mutations ────────────────────────────────────────────────────
  let _suppressRender = false;

  function dispatch(handles) {
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handles } }));
  }

  function addHandle(handle, data) {
    const next = [handle, ...readHandles().filter(h => h !== handle)];
    persistHandles(next);
    if (data) storeItemData(handle, data);
    dispatch(next);
    GA4.addToWishlist(handle, data || getItemData(handle));
    return next;
  }

  function removeHandle(handle, opts = {}) {
    const data = getItemData(handle);
    const next = readHandles().filter(h => h !== handle);
    persistHandles(next);
    if (opts.suppressRender) _suppressRender = true;
    dispatch(next);
    _suppressRender = false;
    if (!opts.silent) GA4.removeFromWishlist(handle, data);
    return next;
  }

  function clearWishlist() {
    const handles = getHandles();
    GA4.clearAll(handles);
    _handles = [];
    _data    = {};
    localStorage.setItem(STORAGE_KEY, '[]');
    localStorage.removeItem(DATA_KEY);
    dispatch([]);
  }

  // ── UI helpers ─────────────────────────────────────────────────────────
  function setHeaderCount(n) {
    document.querySelectorAll(sel.headerCount).forEach(el => {
      el.textContent = n;
      el.hidden      = n <= 0;
      el.setAttribute('aria-label', `${n} wishlist item${n !== 1 ? 's' : ''}`);
    });
  }

  function syncButtons(root = document) {
    root.querySelectorAll(sel.toggleBtn).forEach(btn => {
      const handle = btn.dataset.handle;
      if (!handle) return;
      const inList = isInWishlist(handle);
      btn.setAttribute('aria-pressed', inList ? 'true' : 'false');
      if (btn.hasAttribute('data-wl-pdp')) {
        const textEl = btn.querySelector('[data-wl-text]');
        if (textEl) textEl.textContent = inList ? 'Visit wishlist' : 'Add to wishlist';
        btn.dataset.mode = inList ? 'visit' : 'add';
      }
    });
  }

  // ── Toggle handler ─────────────────────────────────────────────────────
  function onToggle(btn) {
    const handle = btn.dataset.handle;
    if (!handle) return;

    if (btn.hasAttribute('data-wl-pdp') && isInWishlist(handle)) {
      window.location.href = WISHLIST_URL;
      return;
    }

    const adding = btn.getAttribute('aria-pressed') !== 'true';

    btn.setAttribute('aria-pressed', adding ? 'true' : 'false');
    if (btn.hasAttribute('data-wl-pdp')) {
      const textEl = btn.querySelector('[data-wl-text]');
      if (textEl) textEl.textContent = adding ? 'Visit wishlist' : 'Add to wishlist';
      btn.dataset.mode = adding ? 'visit' : 'add';
    }

    if (adding) {
      addHandle(handle, extractItemData(btn));
    } else {
      removeHandle(handle);
    }
  }

  function bindToggleButtons(root = document) {
    root.querySelectorAll(sel.toggleBtn).forEach(btn => {
      if (btn.__wlBound) return;
      btn.__wlBound = true;
      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        onToggle(btn);
      });
    });
  }

  // ── Wishlist page ──────────────────────────────────────────────────────
  const isWishlistPage = !!document.querySelector(sel.wishlistGrid);

  async function fetchCardHTML(handle) {
    const res = await fetch(
      `/products/${encodeURIComponent(handle)}?sections=wishlist-product-card`,
      { credentials: 'same-origin' }
    );
    if (!res.ok) throw new Error(handle);
    const json = await res.json();
    const html = json['wishlist-product-card'];
    console.log(html)
    if (!html) throw new Error(handle);
    return html;
  }

  function updatePageCount(n) {
    document.querySelectorAll(sel.wishlistCount).forEach(el => { el.textContent = n; });
  }

  function bindGridDelegation(grid) {
    if (grid.__wlGridBound) return;
    grid.__wlGridBound = true;

    grid.addEventListener('click', e => {
      const removeBtn = e.target.closest(sel.wishlistRemove);
      if (removeBtn) {
        const handle = removeBtn.dataset.handle;
        if (!handle) return;

        removeBtn.closest('[data-wl-item]')?.remove();
        removeHandle(handle, { suppressRender: true });

        const handles = getHandles();
        setHeaderCount(handles.length);
        updatePageCount(handles.length);

        const isEmpty = handles.length === 0;
        const empty   = document.querySelector(sel.wishlistEmpty);
        if (isEmpty) {
          grid.hidden = true;
          if (empty) empty.hidden = false;
        }

        syncButtons(document);
        return;
      }

      const atcBtn = e.target.closest('button[name="add"], button[data-wl-atc]');
      if (atcBtn) {
        const item   = atcBtn.closest('[data-wl-item]');
        const rmBtn  = item?.querySelector(sel.wishlistRemove);
        const handle = rmBtn?.dataset?.handle;
        if (handle) GA4.wishlistToCart(handle);
      }
    });
  }

  let _rendering = false;

  async function renderWishlistPage() {
    if (!isWishlistPage || _rendering) return;
    _rendering = true;

    try {
      const grid      = document.querySelector(sel.wishlistGrid);
      const empty     = document.querySelector(sel.wishlistEmpty);
      const loadingEl = document.querySelector(sel.wishlistLoading);
      const clearBtn  = document.querySelector(sel.wishlistClear);

      if (!grid) return;

      const handles = getHandles();
      setHeaderCount(handles.length);
      updatePageCount(handles.length);

      grid.hidden    = true;
      grid.innerHTML = '';
      if (empty) empty.hidden = true;

      if (handles.length === 0) {
        if (loadingEl) loadingEl.hidden = true;
        if (empty)     empty.hidden     = false;
        return;
      }

      if (loadingEl) loadingEl.hidden = false;

      const results = await Promise.allSettled(handles.map(fetchCardHTML));
      const frag    = document.createDocumentFragment();

      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          const tmp  = document.createElement('div');
          tmp.innerHTML = r.value;
          const node = tmp.firstElementChild;
          if (node) frag.appendChild(node);
        } else {
          removeHandle(handles[i], { silent: true, suppressRender: true });
        }
      });

      grid.appendChild(frag);

      requestAnimationFrame(() => {
        if (loadingEl) loadingEl.hidden = true;
        grid.hidden = false;
      });

      bindGridDelegation(grid);
      bindToggleButtons(grid);
      syncButtons(grid);

      if (clearBtn && !clearBtn.__wlBound) {
        clearBtn.__wlBound = true;
        clearBtn.addEventListener('click', () => {
          clearWishlist();
          syncButtons(document);
        });
      }
    } finally {
      _rendering = false;
    }
  }

  function safeRender() {
    if (!isWishlistPage || _rendering) return;
    renderWishlistPage();
  }

  // ── Init ───────────────────────────────────────────────────────────────
  function init() {
    setHeaderCount(getHandles().length);
    bindToggleButtons(document);
    syncButtons(document);

    if (isWishlistPage) {
      renderWishlistPage().then(() => GA4.viewWishlist());
    }

    window.addEventListener('wishlist:updated', () => {
      const handles = getHandles();
      setHeaderCount(handles.length);
      syncButtons(document);
      if (!_suppressRender) safeRender();
    });

    window.addEventListener('storage', e => {
      if (e.key !== STORAGE_KEY) return;
      _handles = null;
      _data    = null;
      const handles = getHandles();
      setHeaderCount(handles.length);
      syncButtons(document);
      safeRender();
    });

    // ── Dynamic content (infinite scroll / AJAX filters) ──────────────
    // FIX: accumulate nodes from ALL mutation batches within the debounce
    // window. The previous pattern only processed the LAST batch — any
    // cards added in earlier rapid-fire callbacks were silently dropped,
    // causing wishlist buttons to appear unbound/unsynced on those cards.
    let _obTimer = null;
    let _pendingNodes = new Set();

    const obs = new MutationObserver(mutations => {
      // Collect added element nodes from every callback into the pending set
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) _pendingNodes.add(node);
        }
      }
      clearTimeout(_obTimer);
      _obTimer = setTimeout(() => {
        const nodes = [..._pendingNodes];
        _pendingNodes.clear();
        for (const node of nodes) {
          bindToggleButtons(node);
          syncButtons(node);
        }
      }, 50);
    });

    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
